import {createSmartContext} from '@packages/utilities/smart-context';
import {RefObject, KeyboardEvent} from 'react';
import type {ButtonPrefixSuffix} from '@packages/systems/spring';

export interface TagData {
  id: string;
  type: string;
  label: string;
  status?: 'idle' | 'loading' | 'success' | 'error';
  onDismiss?: () => void;
  onCancel?: () => void;
  // Optional click handler. When set, the chip becomes an interactive button
  // (e.g. paste chips open an edit modal). Undefined for all other consumers,
  // so their chips stay non-interactive.
  onClick?: () => void;
  icon?: ButtonPrefixSuffix;
  imagePreview?: string;
  colorVariant?: 'default' | 'primary' | 'red' | 'green' | 'purple' | 'yellow';
}

export interface TextareaContext {
  value: string;
  isLoading: boolean;
  isDisabled: boolean;
  canSubmit: boolean;
  disabledMessage?: string;
  submitBlockedReason?: string;

  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;

  inputRef: RefObject<HTMLTextAreaElement>;
  tagsRef?: RefObject<HTMLDivElement>;

  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  tags?: TagData[];
}

const TEXTAREA_CONTEXT_ERROR =
  'Textarea components must be used within a Textarea component. ' +
  'Wrap your TextareaInput, TextareaTags, TextareaSubmit, etc. inside <Textarea>.';

const throwContextError = (): never => {
  throw new Error(TEXTAREA_CONTEXT_ERROR);
};

const defaultContext: TextareaContext = {
  value: '',
  tags: undefined,
  isLoading: false,
  canSubmit: false,
  isDisabled: false,
  disabledMessage: undefined,
  submitBlockedReason: undefined,
  tagsRef: undefined,
  onCancel: undefined,
  onKeyDown: undefined,
  inputRef: {current: null},
  onChange: throwContextError,
  onSubmit: throwContextError,
};

const {ContextProvider, useContext} =
  createSmartContext<TextareaContext>(defaultContext);

export const TextareaProvider = ContextProvider;
export const useTextareaContext = useContext;

export const getTags = (ctx: TextareaContext) => ctx.tags;
export const getValue = (ctx: TextareaContext) => ctx.value;
export const getTagsRef = (ctx: TextareaContext) => ctx.tagsRef;
export const getOnChange = (ctx: TextareaContext) => ctx.onChange;
export const getOnSubmit = (ctx: TextareaContext) => ctx.onSubmit;
export const getOnCancel = (ctx: TextareaContext) => ctx.onCancel;
export const getInputRef = (ctx: TextareaContext) => ctx.inputRef;
export const getOnKeyDown = (ctx: TextareaContext) => ctx.onKeyDown;
export const getIsLoading = (ctx: TextareaContext) => ctx.isLoading;
export const getCanSubmit = (ctx: TextareaContext) => ctx.canSubmit;
export const getIsDisabled = (ctx: TextareaContext) => ctx.isDisabled;
export const getDisabledMessage = (ctx: TextareaContext) => ctx.disabledMessage;
export const getSubmitBlockedReason = (ctx: TextareaContext) =>
  ctx.submitBlockedReason;
